import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  Snapshot, Todo,
  TodoRelation, TodoDetail, RelationKind, DetailKind, CacheState,
} from '../../worklog/types';
import './td-catcher.css';

type TodoId = string | number;
type TodoLike = Todo & {
  hot?: boolean;
  priority?: number | null;
  resolved?: boolean | number;
  pending?: boolean;
};

export interface CaptureDraft {
  title: string;
  category: string;
  details?: string;
  tags?: string[];
  commitment?: string | null;
}
type ComposeDraft = CaptureDraft & {
  priority?: number | null;
  hard_deadline?: string;
  soft_deadline?: string;
  parent_id?: number | null;
};

export interface TdCatcherProps {
  snapshot?: Snapshot | null;
  todos?: TodoLike[];
  endpoint?: string;
  onRefresh?: () => Promise<void> | void;
}

const DEFAULT_CATEGORIES = ['Inbox', 'Jenny', 'Alex', 'Build', 'Later'];
const BOARD_LANES = ['Inbox', 'People', 'Projects'] as const;
type Lane = typeof BOARD_LANES[number];
type FilterMode = 'all' | 'hot' | 'people' | 'done';

const RELATION_KINDS: RelationKind[] = [
  'prereq_of', 'depends_on', 'blocks', 'blocked_by',
  'adjacent', 'duplicates', 'references',
];
const RELATION_GLYPHS: Record<RelationKind, string> = {
  prereq_of: '→', depends_on: '←', blocks: '⤬', blocked_by: '⤬',
  adjacent: '↔', duplicates: '≡', references: '↗',
};
const RELATION_LABELS: Record<RelationKind, string> = {
  prereq_of: 'prereq', depends_on: 'depends', blocks: 'blocks', blocked_by: 'blocked-by',
  adjacent: 'adjacent', duplicates: 'duplicate', references: 'ref',
};
const DETAIL_KIND_LABELS: Record<DetailKind, string> = {
  feature: 'Features', challenge: 'Blockers', filepath: 'Files',
  residence: 'Locations', reference: 'References', note: 'Notes', followup: 'Next steps',
};
const DETAIL_KIND_ORDER: DetailKind[] = ['followup', 'feature', 'challenge', 'filepath', 'residence', 'reference', 'note'];

// ── helpers ──────────────────────────────────────────────────────────────

function asResolved(t: TodoLike): boolean {
  const r = t.resolved as unknown;
  return r === true || r === 1;
}
function normalizeTags(tags: TodoLike['tags']): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.filter(Boolean).map(String);
  try { const p = JSON.parse(tags as string); if (Array.isArray(p)) return p.filter(Boolean).map(String); } catch { /* */ }
  return String(tags).split(',').map(s => s.trim()).filter(Boolean);
}
function categoryOf(t: TodoLike): string {
  const c = String(t.category || '').trim();
  return c || 'Inbox';
}
function priorityOf(t: TodoLike): number | null {
  const p = t.priority;
  if (typeof p === 'number') return p;
  if (typeof p === 'string' && /^[0-9]+$/.test(p)) return parseInt(p, 10);
  return null;
}
function isHot(t: TodoLike): boolean {
  if (t.hot) return true;
  if (typeof t.priority === 'number' && t.priority >= 8) return true;
  if (t.hard_deadline) return true;
  return false;
}
function isPeople(t: TodoLike): boolean {
  const c = categoryOf(t).toLowerCase();
  return Boolean(t.commitment) || ['jenny', 'alex', 'mom', 'client', 'parent'].includes(c);
}
function laneOf(t: TodoLike): Lane {
  const c = categoryOf(t);
  if (c === 'Inbox' || c.toLowerCase() === 'uncategorized') return 'Inbox';
  if (isPeople(t)) return 'People';
  return 'Projects';
}
function textSearch(t: TodoLike): string {
  return [t.name, t.details, t.category, t.commitment, t.hard_deadline, ...normalizeTags(t.tags)]
    .join(' ').toLowerCase();
}
function daysUntil(s?: string | null): number | null {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}
function dueLabel(t: TodoLike): string | null {
  const d = daysUntil(t.hard_deadline);
  if (d === null) return null;
  if (d < 0) return `${Math.abs(d)}d late`;
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d <= 7) return `${d}d`;
  return new Date(t.hard_deadline!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function relationsForPin(id: number | string, all?: TodoRelation[]) {
  if (!Array.isArray(all)) return [];
  const i = typeof id === 'string' ? parseInt(id, 10) : id;
  const out: Array<{ glyph: string; label: string; otherId: number; kind: RelationKind }> = [];
  for (const r of all) {
    if (r.from_id === i) out.push({ glyph: RELATION_GLYPHS[r.kind], label: RELATION_LABELS[r.kind], otherId: r.to_id, kind: r.kind });
    else if (r.to_id === i) {
      const inv: Partial<Record<RelationKind, RelationKind>> = {
        prereq_of: 'depends_on', depends_on: 'prereq_of',
        blocks: 'blocked_by', blocked_by: 'blocks', references: 'references',
      };
      const k = inv[r.kind] || r.kind;
      out.push({ glyph: RELATION_GLYPHS[k], label: RELATION_LABELS[k], otherId: r.from_id, kind: k });
    }
  }
  return out;
}
function groupDetails(d?: TodoDetail[]): Map<DetailKind, TodoDetail[]> {
  const m = new Map<DetailKind, TodoDetail[]>();
  if (!Array.isArray(d)) return m;
  for (const x of d) {
    if (!m.has(x.kind)) m.set(x.kind, []);
    m.get(x.kind)!.push(x);
  }
  for (const arr of m.values()) arr.sort((a, b) => a.ord - b.ord);
  return m;
}
function childrenOf(id: number | string, all: TodoLike[]): TodoLike[] {
  const i = typeof id === 'string' ? parseInt(id, 10) : id;
  return all.filter(t => t.parent_id != null && Number(t.parent_id) === i);
}
function todoFromSnapshot(s: Snapshot | null | undefined): TodoLike[] {
  return (s?.todos || []).map(t => ({ ...(t as unknown as TodoLike) }));
}

// Flexible deadline parser ported from the python helper in the work CLI.
// Accepts: '2026-05-26', '20260526', '5-26', '0526', '26', '14:00', '1:20',
// '1:20p', '9a', '12pm', and date+time combinations like '5-26 14:00'.
// Returns canonical "YYYY-MM-DD" or "YYYY-MM-DD HH:MM"; empty on failure.
function parseDeadlineFlexible(input: string): string {
  let raw = (input || '').trim();
  if (!raw) return '';
  const now = new Date();
  const todayY = now.getFullYear();
  const todayM = now.getMonth() + 1;
  const todayD = now.getDate();
  const todayMs = new Date(todayY, todayM - 1, todayD).getTime();

  // strip am/pm suffix
  let ampm: 'a' | 'p' | null = null;
  const apMatch = raw.match(/(?:\s*)([ap])\.?m?\.?\s*$/i);
  if (apMatch) {
    ampm = (apMatch[1].toLowerCase() === 'a' ? 'a' : 'p');
    raw = raw.slice(0, apMatch.index!).trim();
  }

  // pull trailing HH:MM
  let hh: number | null = null;
  let mm: number | null = null;
  const tMatch = raw.match(/(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (tMatch) {
    hh = parseInt(tMatch[1], 10);
    mm = parseInt(tMatch[2], 10);
    raw = raw.slice(0, tMatch.index!).trim();
  } else if (ampm) {
    // bare-hour-with-ampm: "9a" / "12pm" / "5 9p"
    const hourMatch = raw.match(/(\d{1,2})$/);
    if (hourMatch) {
      hh = parseInt(hourMatch[1], 10);
      mm = 0;
      raw = raw.slice(0, hourMatch.index!).trim();
    }
  }

  // remaining = date
  let dateY = todayY, dateM = todayM, dateD = todayD;
  let yearExplicit = false;
  if (raw) {
    let m: RegExpMatchArray | null;
    if ((m = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/))) {
      dateY = +m[1]; dateM = +m[2]; dateD = +m[3]; yearExplicit = true;
    } else if ((m = raw.match(/^(\d{4})(\d{2})(\d{2})$/))) {
      dateY = +m[1]; dateM = +m[2]; dateD = +m[3]; yearExplicit = true;
    } else if ((m = raw.match(/^(\d{1,2})[-/](\d{1,2})$/))) {
      dateM = +m[1]; dateD = +m[2];
    } else if ((m = raw.match(/^(\d{2})(\d{2})$/))) {
      dateM = +m[1]; dateD = +m[2];
    } else if ((m = raw.match(/^(\d)(\d{2})$/))) {
      dateM = +m[1]; dateD = +m[2];
    } else if ((m = raw.match(/^(\d{1,2})$/))) {
      dateD = +m[1];
    } else {
      return '';
    }
  } else if (hh === null) {
    return '';
  }

  let dateMs = new Date(dateY, dateM - 1, dateD).getTime();
  if (Number.isNaN(dateMs)) return '';
  if (dateMs < todayMs && !yearExplicit) {
    dateY = todayY + 1;
    dateMs = new Date(dateY, dateM - 1, dateD).getTime();
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  const dateIso = `${dateY}-${pad(dateM)}-${pad(dateD)}`;
  if (hh === null) return dateIso;

  let hour24: number;
  if (ampm === 'a') {
    hour24 = hh === 12 ? 0 : hh;
  } else if (ampm === 'p') {
    hour24 = hh === 12 ? 12 : (hh < 12 ? hh + 12 : hh);
  } else if (hh > 12) {
    hour24 = hh;
  } else {
    const sameDay = dateMs === todayMs;
    if (sameDay) {
      // pick nearest upcoming
      const baseAm = hh === 12 ? 0 : hh;
      const basePm = hh === 12 ? 12 : (hh + 12) % 24;
      const amTime = new Date(dateY, dateM - 1, dateD, baseAm, mm!).getTime();
      const pmTime = new Date(dateY, dateM - 1, dateD, basePm, mm!).getTime();
      const candidates = [amTime, pmTime].filter(c => c >= now.getTime());
      if (candidates.length > 0) {
        const chosen = Math.min(...candidates);
        const d = new Date(chosen);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } else {
        const t = new Date(dateY, dateM - 1, dateD + 1, baseAm, mm!);
        return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())} ${pad(t.getHours())}:${pad(t.getMinutes())}`;
      }
    } else {
      if (hh === 12) hour24 = 12;
      else if (hh >= 8 && hh <= 11) hour24 = hh;
      else if (hh >= 1 && hh <= 7) hour24 = hh + 12;
      else hour24 = hh;
    }
  }
  if (hour24 < 0 || hour24 > 23) return '';
  return `${dateIso} ${pad(hour24)}:${pad(mm!)}`;
}

async function postMutate(body: unknown): Promise<unknown> {
  const res = await fetch('/api/td/mutate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error || ''; } catch { /* */ }
    throw new Error(`mutate ${res.status}${detail ? ': ' + detail : ''}`);
  }
  return res.json();
}

// ── component ────────────────────────────────────────────────────────────

export default function TdCatcher({
  snapshot: provided, todos: providedTodos,
  endpoint = '/api/td/items',
  onRefresh,
}: TdCatcherProps) {
  const [todos, setTodos] = useState<TodoLike[]>(providedTodos || todoFromSnapshot(provided));
  const [cacheState, setCache] = useState<CacheState>('loading');
  const [err, setErr] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [captureText, setCaptureText] = useState('');
  const [captureCat, setCaptureCat] = useState('Inbox');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (onRefresh) { await onRefresh(); return; }
    setCache('loading');
    try {
      const res = await fetch(endpoint, { cache: 'no-store' });
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      const json = await res.json();
      if (!providedTodos) setTodos(todoFromSnapshot(json as Snapshot));
      setCache((json as { cache?: CacheState }).cache || 'unknown');
      setPendingCount(Number((json as { pending_count?: number }).pending_count) || 0);
      setErr(null);
    } catch (e) { setCache('error'); setErr((e as Error).message); }
  }, [endpoint, onRefresh, providedTodos]);

  useEffect(() => { if (providedTodos) setTodos(providedTodos); }, [providedTodos]);
  useEffect(() => {
    if (provided) { if (!providedTodos) setTodos(todoFromSnapshot(provided)); return; }
    refresh().catch(() => undefined);
    const id = window.setInterval(() => { void refresh(); }, 15_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provided, providedTodos, endpoint]);

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(t => (t === msg ? null : t)), 3500);
  }

  const categories = useMemo(() => {
    const found = new Set(todos.map(categoryOf));
    return Array.from(new Set([...DEFAULT_CATEGORIES, ...found]));
  }, [todos]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return todos
      .filter(t => {
        const r = asResolved(t);
        if (filter === 'done' && !r) return false;
        if (filter !== 'done' && r) return false;
        if (filter === 'hot' && !isHot(t)) return false;
        if (filter === 'people' && !isPeople(t)) return false;
        if (needle && !textSearch(t).includes(needle)) return false;
        if (t.parent_id != null) return false;
        return true;
      })
      .sort((a, b) => {
        const pa = priorityOf(a) ?? 0;
        const pb = priorityOf(b) ?? 0;
        if (pa !== pb) return pb - pa;
        return String(b.created_at || '').localeCompare(String(a.created_at || ''));
      });
  }, [filter, todos, q]);

  const open = todos.filter(t => !asResolved(t));
  const inboxCount = open.filter(t => laneOf(t) === 'Inbox').length;
  const hotCount = open.filter(isHot).length;
  const dueCount = open.filter(t => t.hard_deadline).length;
  const doneCount = todos.length - open.length;

  // Optimistic: apply locally + POST + refresh. On error, refresh restores.
  const optimisticUpdate = useCallback((id: TodoId, patch: Partial<TodoLike>) => {
    setTodos(prev => prev.map(t => t.id === id ? { ...t, ...patch, pending: true } : t));
  }, []);

  const capture = useCallback(async () => {
    const text = captureText.trim();
    if (!text) return;
    const draft = {
      op: 'create' as const,
      title: text.length > 96 ? `${text.slice(0, 96).replace(/\s+\S*$/, '')}...` : text,
      details: text.length > 96 ? text : '',
      category: captureCat,
      tags: captureCat === 'Inbox' ? [] : [captureCat.toLowerCase()],
      commitment: ['Jenny', 'Alex'].includes(captureCat) ? captureCat : null,
    };
    try {
      await postMutate(draft);
      setCaptureText('');
      showToast('Captured. Will sync within ~30 min (or run `td sync`).');
      void refresh();
    } catch (e) { setErr((e as Error).message); }
  }, [captureText, captureCat, refresh]);

  const setResolved = useCallback(async (id: TodoId, resolved: boolean) => {
    if (String(id).startsWith('pending-')) {
      showToast('Pin not yet synced; run `td sync` first.');
      return;
    }
    optimisticUpdate(id, { resolved });
    try {
      await postMutate({ op: 'resolve', pin_id: id, resolved });
      void refresh();
    } catch (e) { setErr((e as Error).message); void refresh(); }
  }, [optimisticUpdate, refresh]);

  const setPriority = useCallback(async (id: TodoId, priority: number | null) => {
    if (String(id).startsWith('pending-')) {
      showToast('Pin not yet synced; run `td sync` first.');
      return;
    }
    optimisticUpdate(id, { priority });
    try {
      await postMutate({ op: 'update', pin_id: id, fields: { priority } });
      void refresh();
    } catch (e) { setErr((e as Error).message); void refresh(); }
  }, [optimisticUpdate, refresh]);

  const updateFields = useCallback(async (id: TodoId, fields: Record<string, unknown>) => {
    if (String(id).startsWith('pending-')) {
      showToast('Pin not yet synced; run `td sync` first.');
      return;
    }
    optimisticUpdate(id, fields as Partial<TodoLike>);
    try {
      await postMutate({ op: 'update', pin_id: id, fields });
      void refresh();
    } catch (e) { setErr((e as Error).message); void refresh(); }
  }, [optimisticUpdate, refresh]);

  const deletePin = useCallback(async (id: TodoId) => {
    if (String(id).startsWith('pending-')) {
      showToast('Pin not yet synced; run `td sync` first.');
      return;
    }
    setTodos(prev => prev.filter(t => t.id !== id));
    try {
      await postMutate({ op: 'delete', pin_id: id });
      void refresh();
    } catch (e) { setErr((e as Error).message); void refresh(); }
  }, [refresh]);

  const composeCreate = useCallback(async (d: ComposeDraft) => {
    try {
      await postMutate({ op: 'create', ...d });
      setComposeOpen(false);
      showToast('Captured. Will sync within ~30 min (or run `td sync`).');
      void refresh();
    } catch (e) { setErr((e as Error).message); }
  }, [refresh]);

  return (
    <div className="td-shell">
      <header className="td-head">
        <h1>td <span className="td-head-sub">personal</span></h1>
        <div className="td-head-actions">
          {pendingCount > 0 && (
            <span className="td-head-pending" title="Pending sync to CLI">
              {pendingCount} pending sync
            </span>
          )}
          <a className="td-btn td-btn-ghost" href="/" title="Main worklog dashboard">Worklog</a>
          <a className="td-btn td-btn-ghost" href="/irp" title="Adobe Replacement Project tracker">IRP</a>
          <a className="td-btn td-btn-ghost" href="https://push-forge.pages.dev" target="_blank" rel="noopener" title="Push command builder">Push</a>
          <button className="td-btn td-btn-ghost" onClick={() => setComposeOpen(true)}>Compose</button>
          <button className="td-btn" onClick={() => setFilter(filter === 'hot' ? 'all' : 'hot')}>
            {filter === 'hot' ? 'Show all' : 'Hot only'}
          </button>
          <button className="td-btn td-btn-primary" onClick={() => refresh().catch(console.error)} disabled={cacheState === 'loading'}>
            {cacheState === 'loading' ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      {err && <div className="td-notice-error">{err}</div>}

      <div className="td-stats">
        <div className="td-stat"><span className="td-stat-num">{open.length}</span><span className="td-stat-label">Open</span></div>
        <div className="td-stat"><span className="td-stat-num">{inboxCount}</span><span className="td-stat-label">Inbox</span></div>
        <div className="td-stat is-hot"><span className="td-stat-num">{hotCount}</span><span className="td-stat-label">Hot</span></div>
        <div className="td-stat is-due"><span className="td-stat-num">{dueCount}</span><span className="td-stat-label">Due</span></div>
        <div className="td-stat is-done"><span className="td-stat-num">{doneCount}</span><span className="td-stat-label">Done</span></div>
      </div>

      <div className="td-capture">
        <input
          type="text"
          value={captureText}
          onChange={e => setCaptureText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); capture().catch(console.error); }
          }}
          placeholder="Capture a thought… (Enter to save)"
        />
        <div className="td-capture-chips">
          {categories.slice(0, 5).map(c => (
            <button key={c} className="td-chip" data-active={captureCat === c} onClick={() => setCaptureCat(c)}>
              {c}
            </button>
          ))}
        </div>
        <button className="td-btn td-btn-primary" onClick={() => capture().catch(console.error)} disabled={!captureText.trim()}>
          Save
        </button>
      </div>

      <div className="td-controls">
        <input className="td-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search title, tags, people…" />
        <div className="td-filters">
          {(['all', 'hot', 'people', 'done'] as FilterMode[]).map(m => (
            <button key={m} className="td-filter" data-active={filter === m} onClick={() => setFilter(m)}>
              {m === 'all' ? 'All' : m[0].toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="td-board">
        {BOARD_LANES.map(lane => {
          const items = visible.filter(t => laneOf(t) === lane);
          return (
            <section className="td-lane" key={lane}>
              <div className="td-lane-head">
                <span className="td-lane-title">{lane}</span>
                <span className="td-lane-count">{items.length}</span>
              </div>
              <div className="td-lane-list">
                {items.length === 0 ? (
                  <div className="td-empty">clear</div>
                ) : (
                  items.map(t => (
                    <TodoRow
                      key={String(t.id)}
                      todo={t}
                      allTodos={todos}
                      categories={categories}
                      expanded={Boolean(expanded[String(t.id)])}
                      onExpand={() => setExpanded(prev => ({ ...prev, [String(t.id)]: !prev[String(t.id)] }))}
                      onResolve={() => setResolved(t.id, !asResolved(t)).catch(console.error)}
                      onSetPriority={p => setPriority(t.id, p).catch(console.error)}
                      onUpdate={fields => updateFields(t.id, fields).catch(console.error)}
                      onDelete={() => deletePin(t.id).catch(console.error)}
                    />
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>

      {composeOpen && (
        <Compose
          categories={categories}
          allTodos={todos}
          onSubmit={composeCreate}
          onClose={() => setComposeOpen(false)}
        />
      )}

      {toast && (
        <div className="td-toast" role="status" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
    </div>
  );
}

// ── card row ─────────────────────────────────────────────────────────────

function TodoRow({ todo, allTodos, categories, expanded, onExpand, onResolve, onSetPriority, onUpdate, onDelete }: {
  todo: TodoLike;
  allTodos: TodoLike[];
  categories: string[];
  expanded: boolean;
  onExpand: () => void;
  onResolve: () => void;
  onSetPriority: (p: number | null) => void;
  onUpdate: (fields: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  const resolved = asResolved(todo);
  const tags = normalizeTags(todo.tags);
  const due = dueLabel(todo);
  const rels = relationsForPin(todo.id, todo.relations);
  const byKind = groupDetails(todo.details_struct);
  const kids = childrenOf(todo.id, allTodos);
  const pri = priorityOf(todo);
  const isPending = Boolean(todo.pending) || String(todo.id).startsWith('pending-');
  const canExpand = true; // always expandable now (editor lives in the expansion)
  const [editPri, setEditPri] = useState(false);
  const [priDraft, setPriDraft] = useState<string>(pri == null ? '' : String(pri));

  function commitPri() {
    setEditPri(false);
    const v = priDraft.trim();
    if (v === '') { onSetPriority(null); return; }
    const n = parseInt(v, 10);
    if (Number.isNaN(n) || n < 0 || n > 9) return;
    onSetPriority(n);
  }

  return (
    <article
      className="td-card"
      data-expanded={expanded}
      data-pending={isPending || undefined}
      onClick={canExpand ? onExpand : undefined}
    >
      <button
        className="td-check"
        data-checked={resolved}
        type="button"
        title={resolved ? 'Reopen' : 'Done'}
        onClick={e => { e.stopPropagation(); onResolve(); }}
      />
      <div className="td-card-body">
        <div className="td-card-line">
          <h3 className="td-card-title" data-done={resolved}>{todo.name}</h3>
          {editPri ? (
            <span className="td-pri td-pri-edit" data-set="true" onClick={e => e.stopPropagation()}>
              <input
                type="number"
                min={0}
                max={9}
                value={priDraft}
                onChange={e => setPriDraft(e.target.value)}
                onBlur={commitPri}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); commitPri(); }
                  if (e.key === 'Escape') { setEditPri(false); setPriDraft(pri == null ? '' : String(pri)); }
                }}
                autoFocus
                aria-label="Priority 0-9"
              />
            </span>
          ) : (
            <button
              type="button"
              className="td-pri"
              data-set={pri != null}
              onClick={e => { e.stopPropagation(); if (isPending) return; setPriDraft(pri == null ? '' : String(pri)); setEditPri(true); }}
              title={isPending ? 'Sync first' : 'Set priority 0-9'}
              disabled={isPending}
            >
              p{pri == null ? '-' : pri}
            </button>
          )}
        </div>
        <div className="td-card-meta-row td-chips">
          {isHot(todo) && <span className="td-chip-mini td-chip-hot">hot</span>}
          {due && <span className="td-chip-mini td-chip-due">{due}</span>}
          {todo.commitment && <span className="td-chip-mini td-chip-person">{todo.commitment}</span>}
          <span className="td-card-cat">{categoryOf(todo)}</span>
          {tags.slice(0, 3).map(tag => <span key={tag} className="td-chip-mini td-chip-tag">#{tag}</span>)}
          {isPending && <span className="td-chip-mini td-chip-pending">syncing</span>}
        </div>
      </div>
      <button
        className="td-expand-btn"
        data-expanded={expanded}
        type="button"
        title={expanded ? 'Collapse' : 'Expand to edit'}
        onClick={e => { e.stopPropagation(); onExpand(); }}
      >
        ⌄
      </button>
      {expanded && (
        <div className="td-card-expand" onClick={e => e.stopPropagation()}>
          {isPending ? (
            <p className="td-card-detail">
              Pin not yet synced to local CLI. Run <code>td sync</code> (or wait ~30 min) to enable editing.
            </p>
          ) : (
            <CardEditor
              todo={todo}
              allTodos={allTodos}
              categories={categories}
              rels={rels}
              byKind={byKind}
              kids={kids}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          )}
        </div>
      )}
    </article>
  );
}

// ── editor inside expanded card ──────────────────────────────────────────

function CardEditor({ todo, allTodos, categories, rels, byKind, kids, onUpdate, onDelete }: {
  todo: TodoLike;
  allTodos: TodoLike[];
  categories: string[];
  rels: Array<{ glyph: string; label: string; otherId: number; kind: RelationKind }>;
  byKind: Map<DetailKind, TodoDetail[]>;
  kids: TodoLike[];
  onUpdate: (fields: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  const id = Number(todo.id);
  const parentOptions = useMemo(
    () => allTodos.filter(t => !asResolved(t) && Number(t.id) !== id && t.parent_id == null),
    [allTodos, id]
  );

  return (
    <div className="td-editor">
      <FieldText
        label="Title"
        value={todo.name || ''}
        onSave={v => onUpdate({ name: v })}
      />
      <FieldText
        label="Category"
        value={String(todo.category || '')}
        list={categories}
        onSave={v => onUpdate({ category: v || null })}
      />
      <FieldTextarea
        label="Description"
        value={String(todo.details || '')}
        onSave={v => onUpdate({ details: v || null })}
      />
      <div className="td-editor-row td-editor-row-2col">
        <FieldDeadline
          label="Hard deadline"
          value={String(todo.hard_deadline || '')}
          onSave={v => onUpdate({ hard_deadline: v || null })}
        />
        <FieldDeadline
          label="Soft deadline"
          value={String(todo.soft_deadline || '')}
          onSave={v => onUpdate({ soft_deadline: v || null })}
        />
      </div>
      <div className="td-editor-row td-editor-row-2col">
        <FieldText
          label="Commitment"
          value={String(todo.commitment || '')}
          onSave={v => onUpdate({ commitment: v || null })}
        />
        <FieldSelect
          label="Parent pin"
          value={todo.parent_id == null ? '' : String(todo.parent_id)}
          options={[{ value: '', label: 'none' }, ...parentOptions.map(p => ({
            value: String(p.id),
            label: `#${p.id} ${(p.name || '').slice(0, 50)}`,
          }))]}
          onSave={v => onUpdate({ parent_id: v === '' ? null : Number(v) })}
        />
      </div>
      <FieldTags
        value={normalizeTags(todo.tags)}
        onSave={tags => onUpdate({ tags })}
      />
      <FieldRelations
        relations={rels}
        allTodos={allTodos}
        myId={id}
        onSave={relations => onUpdate({ relations })}
        currentRaw={todo.relations}
      />
      {kids.length > 0 && (
        <div className="td-editor-row">
          <span className="td-editor-label">Subtasks (read-only — edit on the child pin)</span>
          <ul className="td-card-list">
            {kids.map(c => <li key={String(c.id)}>#{c.id} {c.name}</li>)}
          </ul>
        </div>
      )}
      <FieldStructured
        byKind={byKind}
        onSave={details_struct => onUpdate({ details_struct })}
      />
      <div className="td-editor-tools">
        <button className="td-btn td-btn-danger" type="button" onClick={() => {
          if (window.confirm(`Delete pin #${id} ("${todo.name}")?`)) onDelete();
        }}>
          Delete pin
        </button>
      </div>
    </div>
  );
}

// ── field editors ────────────────────────────────────────────────────────

function FieldText({ label, value, list, onSave }: {
  label: string;
  value: string;
  list?: string[];
  onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const dirty = draft !== value;
  const listId = list ? `td-list-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined;
  return (
    <label className="td-editor-row">
      <span className="td-editor-label">{label}</span>
      <input
        type="text"
        value={draft}
        list={listId}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { if (dirty) onSave(draft); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { setDraft(value); (e.target as HTMLInputElement).blur(); }
        }}
      />
      {list && listId ? (
        <datalist id={listId}>
          {list.map(c => <option key={c} value={c} />)}
        </datalist>
      ) : null}
    </label>
  );
}

function FieldTextarea({ label, value, onSave }: {
  label: string;
  value: string;
  onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const dirty = draft !== value;
  return (
    <label className="td-editor-row">
      <span className="td-editor-label">{label}</span>
      <textarea
        value={draft}
        rows={2}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { if (dirty) onSave(draft); }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setDraft(value); (e.target as HTMLTextAreaElement).blur(); }
        }}
      />
    </label>
  );
}

function FieldSelect({ label, value, options, onSave }: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onSave: (v: string) => void;
}) {
  return (
    <label className="td-editor-row">
      <span className="td-editor-label">{label}</span>
      <select
        value={value}
        onChange={e => onSave(e.target.value)}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function FieldDeadline({ label, value, onSave }: {
  label: string;
  value: string;
  onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [parseErr, setParseErr] = useState<string | null>(null);
  useEffect(() => { setDraft(value); setParseErr(null); }, [value]);

  function commit() {
    const t = draft.trim();
    if (t === '') { if (value !== '') onSave(''); return; }
    const parsed = parseDeadlineFlexible(t);
    if (!parsed) { setParseErr('could not parse'); return; }
    setParseErr(null);
    if (parsed !== value) onSave(parsed);
    setDraft(parsed);
  }

  return (
    <label className="td-editor-row">
      <span className="td-editor-label">{label}</span>
      <input
        type="text"
        value={draft}
        onChange={e => { setDraft(e.target.value); if (parseErr) setParseErr(null); }}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { setDraft(value); setParseErr(null); (e.target as HTMLInputElement).blur(); }
        }}
        placeholder="26 1:20p / 5-26 / 14:00 / blank to clear"
        data-error={parseErr ? 'true' : undefined}
      />
      {parseErr && <span className="td-field-err">{parseErr}</span>}
    </label>
  );
}

function FieldTags({ value, onSave }: { value: string[]; onSave: (tags: string[]) => void }) {
  const [draft, setDraft] = useState('');
  function add() {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) { setDraft(''); return; }
    onSave([...value, v]);
    setDraft('');
  }
  function remove(t: string) {
    onSave(value.filter(x => x !== t));
  }
  return (
    <div className="td-editor-row">
      <span className="td-editor-label">Tags</span>
      <div className="td-tag-edit">
        {value.map(t => (
          <span key={t} className="td-chip-mini td-chip-tag td-tag-rm">
            #{t}
            <button type="button" onClick={() => remove(t)} aria-label="Remove">×</button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
          onBlur={add}
          placeholder="add tag…"
        />
      </div>
    </div>
  );
}

function FieldRelations({ relations, allTodos, myId, currentRaw, onSave }: {
  relations: Array<{ glyph: string; label: string; otherId: number; kind: RelationKind }>;
  allTodos: TodoLike[];
  myId: number;
  currentRaw: TodoRelation[] | undefined;
  onSave: (rels: TodoRelation[]) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [newKind, setNewKind] = useState<RelationKind>('depends_on');
  const [newTarget, setNewTarget] = useState('');

  const others = useMemo(
    () => allTodos.filter(t => Number(t.id) !== myId && !asResolved(t)),
    [allTodos, myId]
  );

  function rawArray(): TodoRelation[] {
    return Array.isArray(currentRaw) ? [...currentRaw] : [];
  }
  function removeRel(otherId: number, kind: RelationKind) {
    // The relation stored on this side could be either direction. Try both.
    const inverse: Partial<Record<RelationKind, RelationKind>> = {
      depends_on: 'prereq_of', prereq_of: 'depends_on',
      blocks: 'blocked_by', blocked_by: 'blocks',
      references: 'references',
    };
    const arr = rawArray().filter(r => {
      if (r.from_id === myId && r.to_id === otherId && r.kind === kind) return false;
      if (r.to_id === myId && r.from_id === otherId && r.kind === (inverse[kind] || kind)) return false;
      return true;
    });
    onSave(arr);
  }
  function addRel() {
    const to = Number(newTarget);
    if (!Number.isFinite(to) || to === myId) return;
    const arr = rawArray();
    arr.push({ kind: newKind, from_id: myId, to_id: to });
    onSave(arr);
    setAddOpen(false);
    setNewTarget('');
    setNewKind('depends_on');
  }

  return (
    <div className="td-editor-row">
      <span className="td-editor-label">Relations</span>
      <div className="td-rel-edit">
        {relations.length === 0 && !addOpen ? <em className="td-rel-empty">none</em> : null}
        {relations.map((r, i) => (
          <span key={`${r.kind}-${r.otherId}-${i}`} className="td-rel" data-kind={r.kind}>
            <span aria-hidden="true">{r.glyph}</span>#{r.otherId} {r.label}
            <button type="button" onClick={() => removeRel(r.otherId, r.kind)} aria-label="Remove">×</button>
          </span>
        ))}
        {addOpen ? (
          <span className="td-rel-add">
            <select value={newKind} onChange={e => setNewKind(e.target.value as RelationKind)}>
              {RELATION_KINDS.map(k => <option key={k} value={k}>{RELATION_LABELS[k]}</option>)}
            </select>
            <select value={newTarget} onChange={e => setNewTarget(e.target.value)}>
              <option value="">pick pin…</option>
              {others.map(o => <option key={String(o.id)} value={String(o.id)}>#{o.id} {(o.name || '').slice(0, 40)}</option>)}
            </select>
            <button type="button" className="td-btn td-btn-primary td-btn-mini" onClick={addRel} disabled={!newTarget}>add</button>
            <button type="button" className="td-btn td-btn-ghost td-btn-mini" onClick={() => { setAddOpen(false); setNewTarget(''); }}>cancel</button>
          </span>
        ) : (
          <button type="button" className="td-btn td-btn-ghost td-btn-mini" onClick={() => setAddOpen(true)}>+ add</button>
        )}
      </div>
    </div>
  );
}

function FieldStructured({ byKind, onSave }: {
  byKind: Map<DetailKind, TodoDetail[]>;
  onSave: (items: TodoDetail[]) => void;
}) {
  function flat(): TodoDetail[] {
    const out: TodoDetail[] = [];
    for (const kind of DETAIL_KIND_ORDER) {
      for (const d of (byKind.get(kind) || [])) out.push(d);
    }
    return out;
  }
  function add(kind: DetailKind, value: string) {
    const v = value.trim();
    if (!v) return;
    const arr = flat();
    const kindCount = arr.filter(d => d.kind === kind).length;
    arr.push({ kind, value: v, ord: kindCount });
    onSave(arr);
  }
  function remove(kind: DetailKind, ord: number) {
    const arr = flat().filter(d => !(d.kind === kind && d.ord === ord));
    // re-densify ord within the kind
    const counters: Partial<Record<DetailKind, number>> = {};
    const re = arr.map(d => {
      const n = (counters[d.kind] || 0);
      counters[d.kind] = n + 1;
      return { ...d, ord: n };
    });
    onSave(re);
  }

  return (
    <div className="td-editor-row">
      <span className="td-editor-label">Structured details</span>
      <div className="td-struct-edit">
        {DETAIL_KIND_ORDER.map(kind => (
          <StructuredKindEditor
            key={kind}
            kind={kind}
            items={byKind.get(kind) || []}
            onAdd={v => add(kind, v)}
            onRemove={ord => remove(kind, ord)}
          />
        ))}
      </div>
    </div>
  );
}

function StructuredKindEditor({ kind, items, onAdd, onRemove }: {
  kind: DetailKind;
  items: TodoDetail[];
  onAdd: (v: string) => void;
  onRemove: (ord: number) => void;
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(items.length > 0);
  return (
    <div className="td-struct-kind">
      <button type="button" className="td-struct-kind-head" onClick={() => setOpen(o => !o)}>
        <span>{DETAIL_KIND_LABELS[kind]}</span>
        <span className="td-struct-kind-count">{items.length}</span>
      </button>
      {open && (
        <div className="td-struct-kind-body">
          {kind === 'followup' ? (
            <ol className="td-card-list td-card-list-numbered">
              {items.map(d => (
                <li key={d.ord}>
                  {d.value}
                  <button type="button" className="td-list-rm" onClick={() => onRemove(d.ord)}>×</button>
                </li>
              ))}
            </ol>
          ) : (
            <ul className="td-card-list">
              {items.map(d => (
                <li key={d.ord}>
                  {d.value}
                  <button type="button" className="td-list-rm" onClick={() => onRemove(d.ord)}>×</button>
                </li>
              ))}
            </ul>
          )}
          <div className="td-struct-add">
            <input
              type="text"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); if (draft.trim()) { onAdd(draft); setDraft(''); } }
              }}
              placeholder={`add ${DETAIL_KIND_LABELS[kind].toLowerCase()}…`}
            />
            <button type="button" className="td-btn td-btn-ghost td-btn-mini" disabled={!draft.trim()} onClick={() => { onAdd(draft); setDraft(''); }}>add</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── compose modal (create) ───────────────────────────────────────────────

function Compose({ categories, allTodos, onSubmit, onClose }: {
  categories: string[];
  allTodos: TodoLike[];
  onSubmit: (d: ComposeDraft) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [category, setCategory] = useState('Inbox');
  const [tagsStr, setTagsStr] = useState('');
  const [commitment, setCommitment] = useState('');
  const [priority, setPriority] = useState('');
  const [hard, setHard] = useState('');
  const [soft, setSoft] = useState('');
  const [parent, setParent] = useState('');
  const [hardErr, setHardErr] = useState<string | null>(null);
  const [softErr, setSoftErr] = useState<string | null>(null);

  const parentOpts = useMemo(
    () => allTodos.filter(t => !asResolved(t) && t.parent_id == null),
    [allTodos]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function submit() {
    const t = title.trim();
    if (!t) return;
    let hardP = '';
    let softP = '';
    if (hard.trim()) {
      hardP = parseDeadlineFlexible(hard);
      if (!hardP) { setHardErr('could not parse'); return; }
    }
    if (soft.trim()) {
      softP = parseDeadlineFlexible(soft);
      if (!softP) { setSoftErr('could not parse'); return; }
    }
    const tags = tagsStr.split(',').map(s => s.trim()).filter(Boolean);
    const pri = priority.trim() === '' ? null : parseInt(priority, 10);
    onSubmit({
      title: t,
      details: details.trim() || undefined,
      category: category || 'Inbox',
      tags: tags.length ? tags : undefined,
      commitment: commitment.trim() || null,
      priority: Number.isFinite(pri as number) ? pri : null,
      hard_deadline: hardP || undefined,
      soft_deadline: softP || undefined,
      parent_id: parent.trim() ? Number(parent) : null,
    });
  }

  return (
    <div className="td-modal-scrim" onClick={onClose}>
      <div className="td-modal" onClick={e => e.stopPropagation()}>
        <div className="td-modal-head">
          <h2>Compose</h2>
          <button className="td-btn td-btn-ghost" onClick={onClose}>×</button>
        </div>
        <div className="td-fields">
          <label className="td-field td-field-wide">
            <span className="td-field-label">Title</span>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} autoFocus />
          </label>
          <label className="td-field">
            <span className="td-field-label">Category</span>
            <input type="text" list="td-cat-list" value={category} onChange={e => setCategory(e.target.value)} />
            <datalist id="td-cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
          </label>
          <label className="td-field">
            <span className="td-field-label">Priority 0-9</span>
            <input type="number" min={0} max={9} value={priority} onChange={e => setPriority(e.target.value)} placeholder="blank = none" />
          </label>
          <label className="td-field td-field-wide">
            <span className="td-field-label">Description</span>
            <textarea value={details} onChange={e => setDetails(e.target.value)} rows={3} />
          </label>
          <label className="td-field">
            <span className="td-field-label">Tags (comma-separated)</span>
            <input type="text" value={tagsStr} onChange={e => setTagsStr(e.target.value)} />
          </label>
          <label className="td-field">
            <span className="td-field-label">Commitment</span>
            <input type="text" value={commitment} onChange={e => setCommitment(e.target.value)} placeholder="jenny / alex" />
          </label>
          <label className="td-field">
            <span className="td-field-label">Hard deadline</span>
            <input type="text" value={hard} onChange={e => { setHard(e.target.value); if (hardErr) setHardErr(null); }} placeholder="26 1:20p / 5-26" data-error={hardErr ? 'true' : undefined} />
            {hardErr && <span className="td-field-err">{hardErr}</span>}
          </label>
          <label className="td-field">
            <span className="td-field-label">Soft deadline</span>
            <input type="text" value={soft} onChange={e => { setSoft(e.target.value); if (softErr) setSoftErr(null); }} placeholder="Optional" data-error={softErr ? 'true' : undefined} />
            {softErr && <span className="td-field-err">{softErr}</span>}
          </label>
          <label className="td-field td-field-wide">
            <span className="td-field-label">Parent pin</span>
            <select value={parent} onChange={e => setParent(e.target.value)}>
              <option value="">none</option>
              {parentOpts.map(p => (
                <option key={String(p.id)} value={String(p.id)}>#{p.id} {p.name?.slice(0, 60)}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="td-modal-tools">
          <button className="td-btn td-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="td-btn td-btn-primary" onClick={submit} disabled={!title.trim()}>
            Create pin
          </button>
        </div>
      </div>
    </div>
  );
}
